import {
  getSensorReadings,
  getSensorTimelineContext,
  SensorReading,
  SensorReadingsResponse,
  SensorTimelineContext,
} from '../api/client';

const DB_NAME = 'roaster-sensor-timeline';
const DB_VERSION = 2;
const READING_STORE = 'readings-v1';
const CONTEXT_STORE = 'context-v2';
const MAX_ENTRIES = 80;
const RECENT_EDGE_MS = 2 * 60 * 1000;

interface BaseEntry {
  key: string;
  device: string;
  start: number;
  end: number;
  accessedAt: number;
}

interface ReadingEntry extends BaseEntry {
  bucketSeconds: number;
  readings: SensorReading[];
}

interface ContextEntry extends BaseEntry {
  bucketSeconds: number;
  context: SensorTimelineContext;
}

function normalizedDevice(device?: string) {
  return device?.trim() || '*';
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      reject(new Error('IndexedDB is unavailable'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(READING_STORE)) db.createObjectStore(READING_STORE, { keyPath: 'key' });
      if (!db.objectStoreNames.contains(CONTEXT_STORE)) db.createObjectStore(CONTEXT_STORE, { keyPath: 'key' });
    };
    request.onsuccess = () => resolve(request.result);
  });
}

async function getEntries<T extends BaseEntry>(storeName: string, device: string): Promise<T[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();
    let matches: T[] = [];
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      matches = (request.result as T[]).filter(entry => entry.device === device);
      const accessedAt = Date.now();
      matches.forEach(entry => store.put({ ...entry, accessedAt }));
    };
    transaction.onerror = () => reject(transaction.error);
    transaction.oncomplete = () => {
      db.close();
      resolve(matches);
    };
  });
}

async function putEntry<T extends BaseEntry>(storeName: string, entry: T): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    transaction.objectStore(storeName).put(entry);
    transaction.onerror = () => reject(transaction.error);
    transaction.oncomplete = () => resolve();
  });
  const entries = await new Promise<BaseEntry[]>((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const request = transaction.objectStore(storeName).getAll();
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result as BaseEntry[]);
  });
  if (entries.length > MAX_ENTRIES) {
    const expired = entries.sort((a, b) => b.accessedAt - a.accessedAt).slice(MAX_ENTRIES);
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      expired.forEach(item => store.delete(item.key));
      transaction.onerror = () => reject(transaction.error);
      transaction.oncomplete = () => resolve();
    });
  }
  db.close();
}

function uncoveredGaps(start: number, end: number, entries: BaseEntry[], revalidateEdge: boolean) {
  const cacheableEnd = revalidateEdge ? Math.min(end, Date.now() - RECENT_EDGE_MS) : end;
  const intervals = entries
    .map(entry => [Math.max(start, entry.start), Math.min(cacheableEnd, entry.end)] as const)
    .filter(([from, to]) => to > from)
    .sort((a, b) => a[0] - b[0]);
  const gaps: Array<[number, number]> = [];
  let cursor = start;
  for (const [from, to] of intervals) {
    if (from > cursor) gaps.push([cursor, from]);
    cursor = Math.max(cursor, to);
  }
  if (cursor < cacheableEnd) gaps.push([cursor, cacheableEnd]);
  if (cacheableEnd < end) gaps.push([cacheableEnd, end]);
  return gaps.filter(([from, to]) => to - from >= 1);
}

function readingKey(reading: SensorReading) {
  return `${reading.device_id}\u0000${reading.register_address}\u0000${reading.time}`;
}

function mergeReadings(entries: ReadingEntry[], fetched: SensorReadingsResponse[], start: number, end: number) {
  const merged = new Map<string, SensorReading>();
  entries.forEach(entry => entry.readings.forEach(reading => merged.set(readingKey(reading), reading)));
  fetched.forEach(response => response.readings.forEach(reading => merged.set(readingKey(reading), reading)));
  return Array.from(merged.values())
    .filter(reading => {
      const time = Date.parse(reading.time);
      return time >= start && time <= end;
    })
    .sort((a, b) => Date.parse(a.time) - Date.parse(b.time));
}

export async function loadSensorReadingsCached(
  start: Date,
  end: Date,
  requiredBucketSeconds: number,
  device?: string,
): Promise<{ readings: SensorReading[]; bucketSeconds: number }> {
  const startMs = start.getTime();
  const endMs = end.getTime();
  const deviceKey = normalizedDevice(device);
  let eligible: ReadingEntry[] = [];
  try {
    eligible = (await getEntries<ReadingEntry>(READING_STORE, deviceKey))
      .filter(entry => entry.bucketSeconds <= requiredBucketSeconds);
  } catch {
    eligible = [];
  }
  const recent = endMs > Date.now() - 5 * 60 * 1000;
  const gaps = uncoveredGaps(startMs, endMs, eligible, recent);
  const fetched = await Promise.all(gaps.map(([from, to]) => getSensorReadings({
    startTime: new Date(from).toISOString(),
    endTime: new Date(to).toISOString(),
    device,
  })));
  await Promise.all(fetched.map(async response => {
    const responseStart = Date.parse(response.start_time) || startMs;
    const responseEnd = Date.parse(response.end_time) || endMs;
    const entry: ReadingEntry = {
      key: `${deviceKey}|${responseStart}|${responseEnd}|${response.bucket_seconds}`,
      device: deviceKey,
      start: responseStart,
      end: responseEnd,
      bucketSeconds: response.bucket_seconds,
      readings: response.readings,
      accessedAt: Date.now(),
    };
    try { await putEntry(READING_STORE, entry); } catch { /* cache is best effort */ }
  }));
  const buckets = [...eligible.map(entry => entry.bucketSeconds), ...fetched.map(item => item.bucket_seconds)];
  return {
    readings: mergeReadings(eligible, fetched, startMs, endMs),
    bucketSeconds: buckets.length ? Math.max(...buckets) : requiredBucketSeconds,
  };
}

function mergeContexts(contexts: SensorTimelineContext[], start: Date, end: Date): SensorTimelineContext {
  const ovens = new Map<string, SensorTimelineContext['ovens'][number]>();
  contexts.forEach(context => context.ovens.forEach(oven => {
    const current = ovens.get(oven.machine_id) ?? { ...oven, blend_intervals: [], images: [] };
    current.modbus_device_id ||= oven.modbus_device_id;
    current.vision_device_id ||= oven.vision_device_id;
    const intervalsByBatch = new Map(current.blend_intervals.map(item => [item.batch_id, item]));
    oven.blend_intervals.forEach(item => {
      const existing = intervalsByBatch.get(item.batch_id);
      if (!existing) {
        current.blend_intervals.push(item);
        intervalsByBatch.set(item.batch_id, item);
      } else {
        existing.start_time = Date.parse(item.start_time) < Date.parse(existing.start_time)
          ? item.start_time
          : existing.start_time;
        existing.end_time = Date.parse(item.end_time) > Date.parse(existing.end_time)
          ? item.end_time
          : existing.end_time;
        existing.status = item.status;
      }
    });
    const imageIds = new Set(current.images.map(item => item.event_id));
    oven.images.forEach(item => {
      if (!imageIds.has(item.event_id)) {
        current.images.push(item);
        imageIds.add(item.event_id);
      }
    });
    current.blend_intervals.sort((a, b) => Date.parse(a.start_time) - Date.parse(b.start_time));
    current.images.sort((a, b) => Date.parse(a.captured_at) - Date.parse(b.captured_at));
    ovens.set(oven.machine_id, current);
  }));
  const startMs = start.getTime();
  const endMs = end.getTime();
  return {
    start_time: start.toISOString(),
    end_time: end.toISOString(),
    image_bucket_seconds: Math.max(0, ...contexts.map(context => context.image_bucket_seconds)),
    ovens: Array.from(ovens.values()).map(oven => ({
      ...oven,
      blend_intervals: oven.blend_intervals
        .filter(item => Date.parse(item.end_time) >= startMs && Date.parse(item.start_time) <= endMs)
        .map(item => ({
          ...item,
          start_time: new Date(Math.max(startMs, Date.parse(item.start_time))).toISOString(),
          end_time: new Date(Math.min(endMs, Date.parse(item.end_time))).toISOString(),
        })),
      images: oven.images.filter(item => {
        const capturedAt = Date.parse(item.captured_at);
        return capturedAt >= startMs && capturedAt <= endMs;
      }),
    })),
  };
}

export async function loadSensorContextCached(
  start: Date,
  end: Date,
  device?: string,
): Promise<SensorTimelineContext> {
  const startMs = start.getTime();
  const endMs = end.getTime();
  const deviceKey = normalizedDevice(device);
  const requiredBucketSeconds = Math.max(1, Math.ceil((endMs - startMs) / 120_000));
  let cached: ContextEntry[] = [];
  try {
    cached = (await getEntries<ContextEntry>(CONTEXT_STORE, deviceKey))
      .filter(entry => entry.bucketSeconds <= requiredBucketSeconds);
  } catch {
    cached = [];
  }
  const recent = endMs > Date.now() - 5 * 60 * 1000;
  const gaps = uncoveredGaps(startMs, endMs, cached, recent);
  const fetched = await Promise.all(gaps.map(([from, to]) =>
    getSensorTimelineContext(new Date(from).toISOString(), new Date(to).toISOString(), device)
  ));
  await Promise.all(fetched.map(async context => {
    const entryStart = Date.parse(context.start_time) || startMs;
    const entryEnd = Date.parse(context.end_time) || endMs;
    const entry: ContextEntry = {
      key: `${deviceKey}|${entryStart}|${entryEnd}|context-v1`,
      device: deviceKey,
      start: entryStart,
      end: entryEnd,
      bucketSeconds: context.image_bucket_seconds,
      context,
      accessedAt: Date.now(),
    };
    try { await putEntry(CONTEXT_STORE, entry); } catch { /* cache is best effort */ }
  }));
  return mergeContexts([...cached.map(entry => entry.context), ...fetched], start, end);
}
